import {
  emptyStatusDashboardFilters,
  type StatusDashboardFilters,
} from "@sd/lib/statusDashboardApi";
import {
  emptyCreatedDateFilter,
  normalizeCreatedDateFilter,
  type CreatedDateFilterState,
} from "@sd/lib/ticketCreatedDateFilter";
import {
  type TrackerReportFilters,
} from "@sd/lib/trackerReportFilters";
import { normalizeMultiFilterValues } from "@sd/lib/ticketFilterApi";
import { sanitizeStatusFilterValues } from "@sd/constants/ticketStatuses";

const TRACKER_REPORT_PROJECT_KEY = "sd:tracker-report:project-id";
const TRACKER_REPORT_LIST_FILTERS_KEY = "sd:tracker-report:list-filters";
const TRACKER_REPORT_RETURN_FILTERS_KEY = "sd:tracker-report:return-filters";
const STATUS_DASHBOARD_FILTERS_KEY = "sd:status-dashboard:return-filters";
const STATUS_DASHBOARD_LIST_FILTERS_KEY = "sd:status-dashboard:list-filters";

export interface TrackerReportTicketListContext {
  projectFilters: string[];
  statusFilters: string[];
  typeFilter: string;
  assigneeFilter: string;
  createdDateFilter: CreatedDateFilterState;
}

export interface StatusDashboardTicketListContext {
  projectFilters: string[];
  statusFilters: string[];
  createdDateFilter: CreatedDateFilterState;
}

export function persistTrackerReportProjectId(projectId: string): void {
  const id = projectId.trim();
  if (!id) return;
  try {
    sessionStorage.setItem(TRACKER_REPORT_PROJECT_KEY, id);
  } catch {
    /* private mode / quota */
  }
}

export function readTrackerReportProjectId(): string {
  try {
    return sessionStorage.getItem(TRACKER_REPORT_PROJECT_KEY)?.trim() ?? "";
  } catch {
    return "";
  }
}

export function persistTrackerReportTicketListContext(
  context: TrackerReportTicketListContext
): void {
  try {
    sessionStorage.setItem(
      TRACKER_REPORT_LIST_FILTERS_KEY,
      JSON.stringify({
        projectFilters: normalizeMultiFilterValues(context.projectFilters),
        statusFilters: sanitizeStatusFilterValues(
          normalizeMultiFilterValues(context.statusFilters)
        ),
        typeFilter: String(context.typeFilter ?? "all").trim() || "all",
        assigneeFilter: String(context.assigneeFilter ?? "all").trim() || "all",
        createdDateFilter: normalizeCreatedDateFilter(context.createdDateFilter),
      })
    );
  } catch {
    /* ignore */
  }
}

export function readTrackerReportTicketListContext(): TrackerReportTicketListContext | null {
  try {
    const raw = sessionStorage.getItem(TRACKER_REPORT_LIST_FILTERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TrackerReportTicketListContext>;
    return {
      projectFilters: normalizeMultiFilterValues(
        Array.isArray(parsed.projectFilters) ? parsed.projectFilters.map(String) : []
      ),
      statusFilters: sanitizeStatusFilterValues(
        normalizeMultiFilterValues(
          Array.isArray(parsed.statusFilters) ? parsed.statusFilters.map(String) : []
        )
      ),
      typeFilter: String(parsed.typeFilter ?? "all").trim() || "all",
      assigneeFilter: String(parsed.assigneeFilter ?? "all").trim() || "all",
      createdDateFilter: normalizeCreatedDateFilter(parsed.createdDateFilter),
    };
  } catch {
    return null;
  }
}

export function persistTrackerReportReturnFilters(
  filters: TrackerReportFilters
): void {
  try {
    sessionStorage.setItem(
      TRACKER_REPORT_RETURN_FILTERS_KEY,
      JSON.stringify({
        projectFilters: normalizeMultiFilterValues(filters.projectFilters),
        assigneeFilter: String(filters.assigneeFilter ?? "all").trim() || "all",
        createdDateFilter: normalizeCreatedDateFilter(filters.createdDateFilter),
      })
    );
  } catch {
    /* ignore */
  }
}

export function readTrackerReportReturnFilters(): TrackerReportFilters | null {
  try {
    const raw = sessionStorage.getItem(TRACKER_REPORT_RETURN_FILTERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<TrackerReportFilters>;
    return {
      projectFilters: normalizeMultiFilterValues(
        Array.isArray(parsed.projectFilters) ? parsed.projectFilters.map(String) : []
      ),
      assigneeFilter: String(parsed.assigneeFilter ?? "all").trim() || "all",
      createdDateFilter: normalizeCreatedDateFilter(parsed.createdDateFilter),
    };
  } catch {
    return null;
  }
}

export function persistStatusDashboardReturnFilters(
  filters: StatusDashboardFilters
): void {
  try {
    sessionStorage.setItem(
      STATUS_DASHBOARD_FILTERS_KEY,
      JSON.stringify({
        projectFilters: filters.projectFilters,
        createdDateFilter: normalizeCreatedDateFilter(filters.createdDateFilter),
      })
    );
  } catch {
    /* ignore */
  }
}

export function readStatusDashboardReturnFilters(): StatusDashboardFilters | null {
  try {
    const raw = sessionStorage.getItem(STATUS_DASHBOARD_FILTERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StatusDashboardFilters>;
    return {
      projectFilters: Array.isArray(parsed.projectFilters)
        ? parsed.projectFilters.map(String)
        : [],
      createdDateFilter: normalizeCreatedDateFilter(parsed.createdDateFilter),
    };
  } catch {
    return null;
  }
}

export function persistStatusDashboardTicketListContext(
  context: StatusDashboardTicketListContext
): void {
  try {
    sessionStorage.setItem(
      STATUS_DASHBOARD_LIST_FILTERS_KEY,
      JSON.stringify({
        projectFilters: normalizeMultiFilterValues(context.projectFilters),
        statusFilters: sanitizeStatusFilterValues(
          normalizeMultiFilterValues(context.statusFilters)
        ),
        createdDateFilter: normalizeCreatedDateFilter(context.createdDateFilter),
      })
    );
  } catch {
    /* ignore */
  }
}

export function readStatusDashboardTicketListContext(): StatusDashboardTicketListContext | null {
  try {
    const raw = sessionStorage.getItem(STATUS_DASHBOARD_LIST_FILTERS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<StatusDashboardTicketListContext>;
    return {
      projectFilters: normalizeMultiFilterValues(
        Array.isArray(parsed.projectFilters) ? parsed.projectFilters.map(String) : []
      ),
      statusFilters: sanitizeStatusFilterValues(
        normalizeMultiFilterValues(
          Array.isArray(parsed.statusFilters) ? parsed.statusFilters.map(String) : []
        )
      ),
      createdDateFilter: normalizeCreatedDateFilter(parsed.createdDateFilter),
    };
  } catch {
    return null;
  }
}

export function emptyStatusDashboardReturnFilters(): StatusDashboardFilters {
  return readStatusDashboardReturnFilters() ?? emptyStatusDashboardFilters();
}

export function applyTrackerReportListContext(
  context: TrackerReportTicketListContext,
  setters: {
    setProjectFilters: (v: string[]) => void;
    setStatusFilters: (v: string[]) => void;
    setTypeFilter: (v: string) => void;
    setAssigneeFilters: (v: string[]) => void;
    setCreatedDateFilter: (v: CreatedDateFilterState) => void;
  }
): void {
  setters.setProjectFilters(context.projectFilters);
  setters.setStatusFilters(context.statusFilters);
  setters.setTypeFilter(context.typeFilter);
  const assignee = context.assigneeFilter?.trim() || "all";
  setters.setAssigneeFilters(
    assignee !== "all" ? [assignee] : []
  );
  setters.setCreatedDateFilter(
    context.createdDateFilter ?? emptyCreatedDateFilter()
  );
}

export function trackerReportReturnFiltersFromListContext(
  context: TrackerReportTicketListContext
): TrackerReportFilters {
  return {
    projectFilters: context.projectFilters,
    assigneeFilter: context.assigneeFilter?.trim() || "all",
    createdDateFilter: normalizeCreatedDateFilter(context.createdDateFilter),
  };
}

export function trackerReportListContextFromDrillDown(input: {
  projectFilters: string[];
  statusFilters: string[];
  typeFilter: string;
  assigneeFilter: string;
  createdDateFilter: CreatedDateFilterState;
}): TrackerReportTicketListContext {
  return {
    projectFilters: input.projectFilters,
    statusFilters: input.statusFilters,
    typeFilter: input.typeFilter,
    assigneeFilter: input.assigneeFilter?.trim() || "all",
    createdDateFilter: normalizeCreatedDateFilter(input.createdDateFilter),
  };
}

export function assigneeFiltersFromTrackerAssigneeFilter(
  assigneeFilter: string
): string[] {
  const assignee = assigneeFilter?.trim() || "all";
  return assignee !== "all" ? [assignee] : [];
}

export function applyStatusDashboardListContext(
  context: StatusDashboardTicketListContext,
  setters: {
    setProjectFilters: (v: string[]) => void;
    setStatusFilters: (v: string[]) => void;
    setCreatedDateFilter: (v: CreatedDateFilterState) => void;
  }
): void {
  setters.setProjectFilters(context.projectFilters);
  setters.setStatusFilters(context.statusFilters);
  setters.setCreatedDateFilter(
    context.createdDateFilter ?? emptyCreatedDateFilter()
  );
}
